import type { ShopConfig, Page } from 'domain-model/types/supplier-gateway';
import loginAware from 'components/LoginAwareComponent';
import HeadElement from 'components/HeadElement';
import _ from 'lodash';
//@ts-ignore
import { Col, Row, Container } from 'reactstrap';
import type { NodeId, SerializedNodes } from '@craftjs/core';
import type { ReactNode } from 'react';
import ArticleCarousel, {
  loadCarouselArticles,
} from '../article/ArticleCarousel';
import Brands from 'components/Brands';
import Suppliers from 'components/Suppliers';
import SwiperBannerComponent from '../SwiperBannerComponent';
import ArticleListComponent from 'components/article/ArticleListComponent';
import {
  TextComponent,
  ImageGroupComponent,
  ImageCarouselComponent,
  TextAndImageComponent,
  CarouselAndImageComponent,
  BannerComponent,
  PartnersDisplayComponent,
} from './template-components';

const componentMap: {
  [key: string]: (props: any, nodeId: NodeId) => ReactNode;
} = {
  HTML: (props, nodeId) => (
    <Col
      key={`${nodeId}-col`}
      className={props.className}
      dangerouslySetInnerHTML={{ __html: props.html }}
    />
  ),
  Text: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <TextComponent key={nodeId} props={props} />
    </Col>
  ),
  ImageGroup: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <ImageGroupComponent key={nodeId} blockGroup={props} />
    </Col>
  ),
  ImageCarousel: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <ImageCarouselComponent
        key={nodeId}
        blockCarousel={props}
        isFluid={false} //fixme
      />
    </Col>
  ),
  TextAndImage: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <TextAndImageComponent key={nodeId} blockTextAndImage={props} />
    </Col>
  ),
  CarouselAndImage: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <CarouselAndImageComponent
        key={nodeId}
        articleCarouselWithImage={props}
      />
    </Col>
  ),
  Banner: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <BannerComponent key={nodeId} banner={props} />
    </Col>
  ),
  PartnersDisplay: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <PartnersDisplayComponent key={nodeId} partnersDisplay={props} />
    </Col>
  ),
  SwiperBanner: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <SwiperBannerComponent key={nodeId} value={props} />
    </Col>
  ),
  ArticleList: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <ArticleListComponent key={nodeId} articleListControllerConfig={props} />
    </Col>
  ),
  BrandList: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <Brands
        key={nodeId}
        initialBrands={props.initialBrands || { rows: [] }}
      />
    </Col>
  ),
  SupplierList: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      <Suppliers
        key={nodeId}
        initialSuppliers={props.initialSuppliers || { rows: [] }}
      />
    </Col>
  ),
  Carousel: (props, nodeId) => (
    <Col key={`${nodeId}-col`} className={props.className}>
      {props.carousels.map((carousel: any) => (
        <ArticleCarousel key={carousel.id} carousel={carousel} />
      ))}
    </Col>
  ),
};

const createComponent = ({
  nodeId = 'ROOT',
  serializedNodes,
}: {
  nodeId?: NodeId;
  serializedNodes: SerializedNodes;
}): ReactNode => {
  const node = serializedNodes[nodeId];
  if (nodeId === 'ROOT') {
    return (
      <>
        {node?.nodes.map(childNodeId => {
          return createComponent({
            nodeId: childNodeId,
            serializedNodes,
          });
        })}
      </>
    );
  }

  switch ((node?.type as any).resolvedName) {
    case 'BootstrapRow':
      return (
        <Container
          fluid={node?.props.fluid}
          key={nodeId}
          className={node?.props.fluid ? 'px-0' : ''}
        >
          <Row className={node?.props.className}>
            {node?.nodes.map(childNodeId => {
              return createComponent({
                nodeId: childNodeId,
                serializedNodes,
              });
            })}
          </Row>
        </Container>
      );
    case 'BootstrapColumn':
      return (
        <Col key={nodeId} className={node?.props.className}>
          {node?.nodes.map(childNodeId => {
            return createComponent({
              nodeId: childNodeId,
              serializedNodes,
            });
          })}
        </Col>
      );
    default:
      const Component = componentMap[(node?.type as any).resolvedName];
      return Component ? Component(node?.props, nodeId) : null;
  }
};

function DragDropTemplate({
  pageConfig,
  shopConfig,
  asPath,
}: {
  pageConfig: Page;
  shopConfig: ShopConfig;
  asPath: string;
}) {
  return (
    <>
      <div id="content">
        <HeadElement
          shopConfig={shopConfig}
          asPath={asPath}
          pageConfig={pageConfig}
        />

        {pageConfig.layout?.serializedNodes &&
          createComponent({
            serializedNodes: pageConfig.layout?.serializedNodes,
          })}
      </div>
    </>
  );
}

const getCarousels = async (carousels: any, req: any) => {
  return await Promise.all(
    carousels.map(async (carousel: { config: any }) => ({
      ...carousel,
      articles: await loadCarouselArticles(
        { ...carousel.config, pageSize: 6 },
        req,
      ),
    })),
  );
};

DragDropTemplate.getInitialProps = async ({
  req,
  pageConfig,
  shopConfig,
  asPath,
}: any) => {
  for (const node of _.values(pageConfig.layout.serializedNodes)) {
    if ((node.type as any).resolvedName === 'CarouselAndImage') {
      node.props.articles = await loadCarouselArticles(
        { query: node.props.query, pageSize: 6 },
        req,
      );
    }
    if ((node.type as any).resolvedName === 'Carousel') {
      node.props.carousels = await getCarousels(node.props.carousels, req);
    }

    if ((node.type as any).resolvedName === 'BrandList') {
      const props = await Brands.getInitialProps({
        req,
        querySpecification: {},
        pageConfig,
      });
      node.props.initialBrands = props.initialBrands;
    }
    if ((node.type as any).resolvedName === 'SupplierList') {
      const props = await Suppliers.getInitialProps({
        req,
        querySpecification: {},
        pageConfig,
      });
      node.props.initialSuppliers = props.initialSuppliers;
    }
  }

  return {
    shopConfig,
    pageConfig,
    asPath,
    ...pageConfig,
  };
};

export default loginAware(DragDropTemplate);
